import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import PullToRefresh from '../../components/common/PullToRefresh'
import { useForm } from 'react-hook-form'
import { apiPost } from '../../services/api'
import {
  Lock,
  CheckCircle,
  AlertCircle,
  LogOut,
  ChevronLeft,
  Mail,
  Shield,
  Building2,
  BadgeCheck,
  Eye,
  EyeOff,
  KeyRound,
  ChevronRight,
} from 'lucide-react'

interface PasswordForm {
  current_password: string
  new_password: string
  confirm_password: string
}

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuthStore()
  const navigate = useNavigate()
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCurrentPw, setShowCurrentPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)
  const [showConfirmPw, setShowConfirmPw] = useState(false)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting }, watch } = useForm<PasswordForm>()
  const newPassword = watch('new_password')

  const onSubmit = async (data: PasswordForm) => {
    setError(null)
    setSuccess(null)
    try {
      await apiPost('/auth/change-password', {
        current_password: data.current_password,
        new_password: data.new_password,
      })
      setSuccess('Password changed successfully')
      reset()
      setIsChangingPassword(false)
    } catch {
      setError('Failed to change password. Check your current password.')
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getRoleBadgeColor = (role: string) => {
    const r = role?.toLowerCase()
    switch (r) {
      case 'superadmin': return 'bg-purple-100 text-purple-700'
      case 'company_admin':
      case 'admin': return 'bg-blue-100 text-blue-700'
      case 'supervisor': return 'bg-amber-100 text-amber-700'
      case 'employee': return 'bg-green-100 text-green-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const refreshProfile = useCallback(async () => {
    await checkAuth()
  }, [checkAuth])

  // Use EV role/designation when available (for Eagle Vision users)
  const displayRole = user?.ev_role || user?.role || ''
  const displayDesignation = user?.ev_designation_name

  return (
    <PullToRefresh onRefresh={refreshProfile}>
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 text-white px-4 pt-8 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Profile</h1>
        </div>

        {/* Avatar + Name */}
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-white/20 border-3 border-white/40 flex items-center justify-center mb-3">
            <span className="text-2xl font-bold">{getInitials(user?.name || 'U')}</span>
          </div>
          <h2 className="text-xl font-bold">{user?.name}</h2>
          <p className="text-primary-100 text-sm mt-0.5">{user?.email}</p>
          <span className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(displayRole)}`}>
            {displayRole.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Info Card - overlapping header */}
      <div className="px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Info rows */}
          <div className="divide-y divide-gray-100">
            <div className="flex items-center gap-3 px-4 py-3.5">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4.5 w-4.5 text-blue-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400">Email</p>
                <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3.5">
              <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                <Shield className="h-4.5 w-4.5 text-purple-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400">Role</p>
                <p className="text-sm font-medium text-gray-900 capitalize">{displayRole.replace('_', ' ')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-4 py-3.5">
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4.5 w-4.5 text-green-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400">Company</p>
                <p className="text-sm font-medium text-gray-900">{user?.company?.name || 'N/A'}</p>
              </div>
            </div>

            {displayDesignation && (
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="h-4.5 w-4.5 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-400">Designation</p>
                  <p className="text-sm font-medium text-gray-900">{displayDesignation}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success / Error banners */}
      {success && (
        <div className="px-4 mt-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            {success}
          </div>
        </div>
      )}
      {error && (
        <div className="px-4 mt-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        </div>
      )}

      {/* Actions Section */}
      <div className="px-4 mt-6">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Account</p>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-100">
          {/* Change Password */}
          {!isChangingPassword ? (
            <button
              onClick={() => { setIsChangingPassword(true); setSuccess(null); setError(null) }}
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <KeyRound className="h-4.5 w-4.5 text-indigo-500" />
              </div>
              <span className="text-sm font-medium text-gray-900 flex-1 text-left">Change Password</span>
              <ChevronRight className="h-4 w-4 text-gray-300" />
            </button>
          ) : (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-4 w-4 text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-900">Change Password</h3>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                {/* Current password */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 ${
                        errors.current_password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      placeholder="Enter current password"
                      {...register('current_password', { required: 'Required' })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.current_password && <p className="mt-1 text-xs text-red-500">{errors.current_password.message}</p>}
                </div>

                {/* New password */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 ${
                        errors.new_password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      placeholder="Min. 8 characters"
                      {...register('new_password', {
                        required: 'Required',
                        minLength: { value: 8, message: 'Min 8 characters' },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.new_password && <p className="mt-1 text-xs text-red-500">{errors.new_password.message}</p>}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10 ${
                        errors.confirm_password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      placeholder="Re-enter new password"
                      {...register('confirm_password', {
                        required: 'Required',
                        validate: (value) => value === newPassword || 'Passwords do not match',
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPw(!showConfirmPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showConfirmPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.confirm_password && <p className="mt-1 text-xs text-red-500">{errors.confirm_password.message}</p>}
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setIsChangingPassword(false); reset(); setShowCurrentPw(false); setShowNewPw(false); setShowConfirmPw(false) }}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Logout */}
      <div className="px-4 mt-6">
        <button
          onClick={() => { logout(); navigate('/login') }}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-white border border-red-200 text-red-600 rounded-2xl font-medium text-sm hover:bg-red-50 active:bg-red-100 transition-colors shadow-sm"
        >
          <LogOut className="h-4.5 w-4.5" />
          Logout
        </button>
      </div>

      {/* App version */}
      <div className="text-center mt-6">
        <p className="text-xs text-gray-300">AEON KPI v1.0</p>
      </div>
    </div>
    </PullToRefresh>
  )
}
